import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, XCircle, Trash2, Save, User, Clock, Calendar as CalIcon, Sparkles, MessageSquare } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/tz";

// Reuse the same "default + durations[]" merge as the quick-book dialog so the
// edit UI shows exactly the pill row operators expect.
function durationOptionsFor(treatment) {
  if (!treatment) return [];
  const base = { minutes: Number(treatment.duration_min) || 60, price: Number(treatment.price) || 0, label: "" };
  const extras = (treatment.durations || [])
    .map((d) => ({ minutes: Number(d.minutes) || 0, price: Number(d.price) || 0, label: (d.label || "").trim() }))
    .filter((d) => d.minutes > 0);
  const seen = new Set();
  const merged = [base, ...extras].filter((d) => (seen.has(d.minutes) ? false : (seen.add(d.minutes), true)));
  merged.sort((a, b) => a.minutes - b.minutes);
  return merged;
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Booking actions dialog — opens when the operator taps a tile in Spa Ops.
 * Shows read-only booking info + one of three modes depending on button clicks:
 *   - default: Confirm / Edit / Cancel / Delete
 *   - edit: form (time, duration, therapist, notes) + Save
 *   - already-confirmed: shows "Confirmed by X" line; Edit / Cancel / Delete
 *     stay available so operators can still make changes.
 *
 * Eligibility rules (mirror the backend so the UI reflects reality):
 *   - Confirm: developer / admin / manager / supervisor / spa_ops always;
 *     crew only if they're directly assigned or in the treatment pool.
 *   - Edit / Cancel: same roles as manual-booking permission (backend gates it).
 *   - Delete: developer / admin only — a soft "Cancel" is safer for anyone else.
 */
export default function SpaOpsBookingActions({ open, onOpenChange, booking, treatments, crew, onChanged }) {
  const { admin } = useAuth();
  const [mode, setMode] = useState("default");   // 'default' | 'edit'
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ start_at: "", crew_id: "", duration_min: 60, notes: "" });

  useEffect(() => {
    if (!open || !booking) return;
    setMode("default");
    setForm({
      start_at: toLocalInput(booking.start_at),
      crew_id: booking.crew_id || "",
      duration_min: booking.duration_min || 60,
      notes: booking.notes || "",
    });
  }, [open, booking]);

  const treatment = useMemo(
    () => (treatments || []).find((t) => t.id === booking?.treatment_id),
    [treatments, booking?.treatment_id],
  );
  const durationChoices = useMemo(() => durationOptionsFor(treatment), [treatment]);
  const eligibleCrew = useMemo(() => {
    const active = (crew || []).filter((c) => c.active !== false);
    if (!treatment) return active;
    const pool = treatment.crew_ids || [];
    return pool.length ? active.filter((c) => pool.includes(c.id)) : active;
  }, [crew, treatment]);

  const role = admin?.role;
  const isOps = ["developer", "admin", "manager", "supervisor", "spa_ops", "pantry"].includes(role);
  const canDelete = ["developer", "admin"].includes(role);
  // Confirm eligibility (mirrors backend). Non-crew ops roles always allowed;
  // crew need to match crew_id assignment OR the treatment's crew pool.
  // NOTE: MUST run every render — never guard behind an `if (!booking) return null`
  // above it, or React throws "Rendered more hooks than during the previous render".
  const canConfirm = useMemo(() => {
    if (!booking || booking.confirmed_at) return false;
    if (isOps) return true;
    if (role !== "crew") return false;
    const mine = admin?.crew_id;
    if (!mine) return false;
    if (booking.crew_id === mine) return true;
    const pool = treatment?.crew_ids || [];
    return pool.length === 0 || pool.includes(mine);
  }, [booking, isOps, role, admin?.crew_id, treatment]);
  const canEdit = isOps && !!booking;
  const canCancel = isOps && !!booking && booking.status !== "cancelled";

  if (!booking) return null;

  const runConfirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.id}/confirm`);
      toast.success(data.already ? "Already confirmed" : `Confirmed by ${data.confirmed_by_name || admin?.name || "you"}`);
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not confirm booking");
    } finally { setSaving(false); }
  };

  const runCancel = async () => {
    if (!window.confirm("Cancel this booking? The guest will keep the record but the slot frees up.")) return;
    setSaving(true);
    try {
      await api.put(`/admin/bookings/${booking.id}`, { status: "cancelled" });
      toast.warning("Booking cancelled");
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not cancel booking");
    } finally { setSaving(false); }
  };

  const runDelete = async () => {
    if (!window.confirm("Delete this booking permanently? The audit trail will still record the deletion.")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/bookings/${booking.id}`);
      toast.warning("Booking deleted");
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not delete booking");
    } finally { setSaving(false); }
  };

  const runSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {};
      const newIso = form.start_at ? new Date(form.start_at).toISOString() : null;
      if (newIso && newIso !== booking.start_at) payload.start_at = newIso;
      if ((form.crew_id || null) !== (booking.crew_id || null)) payload.crew_id = form.crew_id || null;
      if (Number(form.duration_min) !== Number(booking.duration_min)) payload.duration_min = Number(form.duration_min);
      if ((form.notes || "") !== (booking.notes || "")) payload.notes = form.notes;
      if (Object.keys(payload).length === 0) {
        toast("No changes to save");
        setMode("default");
        return;
      }
      await api.put(`/admin/bookings/${booking.id}`, payload);
      toast.success("Booking updated");
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not update booking");
    } finally { setSaving(false); }
  };

  const crewName = (crew || []).find((c) => c.id === booking.crew_id)?.name || "Any therapist";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="spa-ops-booking-actions">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" /> {booking.guest_name}
          </DialogTitle>
          <div className="pt-2 space-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><CalIcon className="h-3.5 w-3.5 text-primary/60" /> {fmtDateTime(booking.start_at)}</div>
            <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-primary/60" /> {booking.duration_min} min · {booking.treatment_name}</div>
            <div className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-primary/60" /> {crewName}</div>
            {booking.notes && (
              <div className="flex items-start gap-2 mt-2 text-xs bg-muted/30 border-l-2 border-accent/40 px-2 py-1.5">
                <MessageSquare className="h-3 w-3 mt-0.5 text-accent flex-shrink-0" />
                <span className="whitespace-pre-wrap">{booking.notes}</span>
              </div>
            )}
            {booking.confirmed_at && (
              <div className="mt-2 pt-2 border-t border-border flex items-center gap-2 text-emerald-600 dark:text-emerald-400" data-testid="ba-confirmed-line">
                <Check className="h-3.5 w-3.5" /> Confirmed by <span className="font-medium">{booking.confirmed_by_name || "Crew"}</span>
                <span className="opacity-60 text-xs ml-1">· {fmtDateTime(booking.confirmed_at)}</span>
              </div>
            )}
            {booking.status === "cancelled" && (
              <div className="mt-2 pt-2 border-t border-border flex items-center gap-2 text-destructive">
                <XCircle className="h-3.5 w-3.5" /> This booking is cancelled.
              </div>
            )}
          </div>
        </DialogHeader>

        {mode === "default" && (
          <div className="grid grid-cols-2 gap-2 pt-2" data-testid="ba-actions">
            {canConfirm && (
              <Button onClick={runConfirm} disabled={saving} className="bg-accent hover:bg-accent/90 text-accent-foreground col-span-2" data-testid="ba-confirm">
                <Check className="h-4 w-4 mr-2" /> Confirm
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" onClick={() => setMode("edit")} disabled={saving} data-testid="ba-edit">
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </Button>
            )}
            {canCancel && (
              <Button variant="outline" onClick={runCancel} disabled={saving} className="text-amber-600 dark:text-amber-500 border-amber-600/40" data-testid="ba-cancel-booking">
                <XCircle className="h-4 w-4 mr-2" /> Cancel
              </Button>
            )}
            {canDelete && (
              <Button variant="outline" onClick={runDelete} disabled={saving} className="text-destructive border-destructive/40 col-span-2" data-testid="ba-delete">
                <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
              </Button>
            )}
            {!canConfirm && !canEdit && !canCancel && !canDelete && (
              <p className="col-span-2 text-xs text-muted-foreground italic">
                No actions available to you for this booking.
              </p>
            )}
          </div>
        )}

        {mode === "edit" && (
          <div className="space-y-4 pt-2" data-testid="ba-edit-form">
            <div>
              <Label className="overline text-primary/60 flex items-center gap-1.5"><CalIcon className="h-3 w-3" /> Date & time</Label>
              <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="mt-2 h-11" data-testid="ba-edit-when" />
            </div>
            {durationChoices.length > 1 && (
              <div>
                <Label className="overline text-primary/60 flex items-center gap-1.5"><Clock className="h-3 w-3" /> Duration</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {durationChoices.map((d) => {
                    const active = Number(form.duration_min) === d.minutes;
                    return (
                      <button
                        key={d.minutes}
                        type="button"
                        onClick={() => setForm({ ...form, duration_min: d.minutes })}
                        className={`px-4 h-10 rounded-full border text-sm transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/60 hover:bg-muted"}`}
                        aria-pressed={active}
                        data-testid={`ba-edit-duration-${d.minutes}`}
                      >
                        <span className="font-mono">{d.minutes}</span><span className="opacity-70 ml-1">min</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="overline text-primary/60 flex items-center gap-1.5"><User className="h-3 w-3" /> Therapist</Label>
              <select
                value={form.crew_id}
                onChange={(e) => setForm({ ...form, crew_id: e.target.value })}
                className="mt-2 w-full h-11 border border-border bg-background px-3 text-sm"
                data-testid="ba-edit-crew"
              >
                <option value="">— any available —</option>
                {eligibleCrew.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="overline text-primary/60 flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" data-testid="ba-edit-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMode("default")} disabled={saving} data-testid="ba-edit-back">Back</Button>
              <Button onClick={runSaveEdit} disabled={saving} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="ba-edit-save">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
